import { appstr } from "../utils/appstr";
import { createBoldToastMessage } from "../utils/createBoldToastMessage";
import {
  createEmployeePositionClickableToast,
  createPositionClickableToast,
  createMovedFromToPositionClickableToast,
  createEntityUpdatedClickableToast
} from "../utils/clickableToastHandlers";
import {
  AssignmentStatus,
  enumObjClassNames,
  enumOverrideType,
  kEmployeeAssignmentStatusID,
  kEmployeePositionID,
  kPositionStatusID,
  PositionStatus
} from "../constants";
import { isInTabularView } from "../utils/isInTabularView";
import { IsPositionClosed } from "../utils/IsPositionClosed";
import { FormatPositionLabel } from "../utils/FormatPositionLabel";
import { sanitizeBlockedValue } from "../utils/FormatFieldValue";
import { Globals } from "./Globals";
import { QAlert } from "./QAlert";
import { QChange } from "./QChange";
import type { QObjDB } from "./QObjDB";
import { QObjDBObject } from "./QObjDBObject";
import type { QOverrideMgr } from "./QOverrideMgr";
import type { QPlanManager } from "./QPlanManager";
import type { QXWSMgr } from "./QXWSMgr";

type InvalidContext = {
  isValid: false;
  wsMgr: QXWSMgr | null;
  planMgr: QPlanManager | null;
  overrideClass: QOverrideMgr | null;
  objDb: QObjDB | null;
  isPlan: boolean;
};

type ValidContext = {
  isValid: true;
  wsMgr: QXWSMgr;
  planMgr: QPlanManager | null;
  overrideClass: QOverrideMgr;
  objDb: QObjDB;
  isPlan: boolean;
};

type Context = InvalidContext | ValidContext;

export class OverrideFactory {
  private static getContext(): Context {
    const wsMgr = Globals._wsMgr;
    const planMgr = wsMgr?.GetPlanMgr() ?? null;
    const overrideClass = planMgr?.fOverrideClass ?? wsMgr?.GetDoc?.()?.GetOverrideClass() ?? null;
    const objDb = overrideClass?.GetObjDB() ?? null;

    if (wsMgr !== null && objDb !== null && overrideClass !== null) {
      return {
        wsMgr,
        planMgr,
        objDb,
        overrideClass,
        isValid: true,
        isPlan: planMgr !== null
      };
    }

    return {
      wsMgr,
      planMgr,
      objDb,
      overrideClass,
      isValid: false,
      isPlan: planMgr !== null && planMgr !== undefined
    };
  }

  private static createChangeDict(
    { wsMgr }: ValidContext,
    data: Record<string, unknown>,
    actionID: string | null = null
  ) {
    return {
      author: wsMgr.GetUserName(),
      date: new Date().toUTCString(),
      actionID,
      data
    };
  }

  private static createOverrideWithChange(
    ctx: ValidContext,
    dataObj: QObjDBObject,
    overrideType: string,
    changeData: Record<string, unknown>,
    actionID?: string
  ) {
    const { wsMgr, overrideClass } = ctx;
    if (!ctx.planMgr) {
      throw new Error("Plan Manager is not available in the current context.");
    }
    const overrideObj = ctx.planMgr.GetActiveOverrideOrCreate(dataObj) ?? new QObjDBObject();
    overrideObj.SmartSetFieldValue("Type", overrideType);

    const changeDict = this.createChangeDict(ctx, changeData, actionID);
    const change = new QChange();
    change.InitChange(wsMgr, changeDict, overrideObj, dataObj, overrideClass);
    change.UpdateChange(changeDict, overrideType);
    overrideObj.fChanges?.push(change);

    return overrideObj;
  }

  private static async sendBatchUpdate(ctx: ValidContext, overrides: QObjDBObject[]) {
    const { planMgr } = ctx;
    if (!planMgr || overrides.length === 0) {
      return false;
    }

    try {
      const response = await planMgr.SendUpdate(overrides, false, false, false);
      const result = JSON.parse(response);
      return result["success"] === true;
    } catch {
      return false;
    }
  }

  private static findFieldNameById(
    ctx: ValidContext,
    className: string,
    fieldId: string
  ): string | null {
    const objClass = ctx.objDb?.GetClassByName(className);
    if (!objClass) {
      return null;
    }

    const fieldDef = objClass.GetFieldDefByID(fieldId);
    return fieldDef?.GetFieldName() ?? null;
  }

  static async UnassignHolders(dataObjs: QObjDBObject[], partialUnassign?: boolean) {
    const ctx = this.getContext();
    if (!ctx.isValid) {
      return false;
    }

    const objDb = ctx.overrideClass?.GetObjDB();
    const boxClass = objDb?.GetClassByName(enumObjClassNames.box);
    if (!boxClass) {
      return false;
    }

    const empPosFieldName = this.findFieldNameById(ctx, enumObjClassNames.box, kEmployeePositionID);
    if (!empPosFieldName) {
      return false;
    }

    if (dataObjs.length === 0) {
      return false;
    }

    let messagingMode: "all" | "single" | "mixed" | null = null;
    if (dataObjs.length === 1) {
      messagingMode = "single";
    } else {
      const firstPositionID = dataObjs[0].SmartGetFieldValue(kEmployeePositionID);
      const areAllSamePosition = dataObjs.every(dataObj => {
        return dataObj.SmartGetFieldValue(kEmployeePositionID) === firstPositionID;
      });

      if (areAllSamePosition) {
        messagingMode = "all";
      } else {
        messagingMode = "mixed";
      }
    }

    const overrides: QObjDBObject[] = [];
    let success = false;
    let firstElementParentPosition = null;

    if (ctx.isPlan) {
      const actionID = crypto.randomUUID();

      for (const dataObj of dataObjs) {
        const overrideObj = this.createOverrideWithChange(
          ctx,
          dataObj,
          enumOverrideType.move,
          {
            [empPosFieldName]: "",
            [kEmployeeAssignmentStatusID]: AssignmentStatus.Unassigned,
            Orphan: 1
          },
          actionID
        );

        overrides.push(overrideObj);
      }

      success = await this.sendBatchUpdate(ctx, overrides);
    } else {
      if (dataObjs.length) firstElementParentPosition = dataObjs[0].GetParentPosition();
      for (const dataObj of dataObjs) {
        // Remove the holder from its parent position's holder list before the override
        // clears Employee PositionID, so peer/count computations stay live and correct.
        const parentPos = dataObj.GetParentPosition?.();
        if (parentPos) {
          const holderList = parentPos.GetHolderList?.() ?? [];
          parentPos.SetHolderList?.(holderList.filter((h: any) => h !== dataObj));
        }
        ctx.overrideClass.SetBoxOverride(dataObj, enumOverrideType.move, {
          data: {
            [empPosFieldName]: "",
            [kEmployeeAssignmentStatusID]: AssignmentStatus.Unassigned,
            Orphan: 1
          }
        });
      }

      success = true;
    }

    let toastTemplateKey = "";
    let toastReplaceDict = {};
    if (messagingMode === "single") {
      const [holderObj] = dataObjs;
      const positionObj = firstElementParentPosition ?? holderObj.GetParentPosition();
      const employeeName = sanitizeBlockedValue(holderObj.SmartGetFieldValue("Name"));

      if (success) {
        // Temporarily make employee not clickable from toast till Employee list is not added into Tabular view.
        // TODO: After Employee list is added, remove below block.
        if (isInTabularView()) {
          if (!positionObj) {
            // positionObj unavailable in tabular view; suppress clickable toast
          } else {
            ctx.wsMgr.BroadcastTopic("add-toast", {
              content: createEntityUpdatedClickableToast(positionObj, {
                template: "Employee is unassigned from entity",
                value: { employee: employeeName as any }
              }),
              status: "success",
              timeoutMS: 10000
            });
          }
        } else {
          const positionId = positionObj?.SmartGetFieldValue("PositionID");
          ctx.wsMgr.BroadcastTopic("add-toast", {
            content: createEmployeePositionClickableToast(
              "{employee} is unassigned from {position}",
              employeeName as any,
              positionObj ? FormatPositionLabel(positionObj) : "",
              positionId as any
            ),
            status: "success",
            timeoutMS: 10000
          });
        }
      }
    } else if (messagingMode === "all") {
      const [holderObj] = dataObjs;
      const positionObj = firstElementParentPosition ?? holderObj.GetParentPosition();

      const totalHoldersCount = partialUnassign ? dataObjs.length : appstr("All");
      toastTemplateKey = "totalHoldersCount employees are unassigned from entity";
      toastReplaceDict = {
        totalHoldersCountEmployees: `${totalHoldersCount} ${appstr(totalHoldersCount === 1 ? "Employee" : "Employees").toLocaleLowerCase()}`
      };

      if (success) {
        ctx.wsMgr.BroadcastTopic("add-toast", {
          content: createEntityUpdatedClickableToast(positionObj, {
            template: toastTemplateKey,
            value: toastReplaceDict
          }),
          status: "success",
          timeoutMS: 10000
        });
      }
    } else if (messagingMode === "mixed") {
      const totalHoldersCount = partialUnassign ? dataObjs.length : appstr("All");
      toastTemplateKey = "totalHoldersCount employees are unassigned from their positions";
      toastReplaceDict = {
        totalHoldersCountEmployees: `${totalHoldersCount} ${appstr(totalHoldersCount === 1 ? "Employee" : "Employees").toLocaleLowerCase()}`
      };

      if (success) {
        ctx.wsMgr.BroadcastTopic("add-toast", {
          ...createBoldToastMessage(toastTemplateKey, toastReplaceDict),
          status: "success"
        });
      }
    }

    if (!success) {
      ctx.wsMgr.BroadcastTopic("add-toast", {
        message: appstr("Unable to Save Changes"),
        status: "error"
      });
    }

    return success;
  }

  static async ReassignHolder(holderObj: QObjDBObject, positionObj: QObjDBObject) {
    if (!holderObj || !positionObj) {
      return false;
    }

    const ctx = this.getContext();
    if (!ctx.isValid) {
      return false;
    }

    if (positionObj.SmartGetFieldValue(kPositionStatusID) === PositionStatus.Closed) {
      const alert = new QAlert();
      alert.InitAlert(
        appstr("Cannot move record"),
        appstr("You cannot move employees to closed positions."),
        null,
        ctx.wsMgr
      );
      return false;
    }

    const empPosFieldName = this.findFieldNameById(ctx, enumObjClassNames.box, kEmployeePositionID);
    if (!empPosFieldName) {
      return false;
    }

    const targetPositionID = positionObj.GetObjectRecordID();
    const currentPosition = holderObj.GetParentPosition();
    if (currentPosition?.GetObjectRecordID() === targetPositionID) {
      return false;
    }

    const changeDict = {
      [empPosFieldName]: targetPositionID
    };

    if (holderObj.SmartGetFieldValue(kEmployeeAssignmentStatusID) !== AssignmentStatus.Assigned) {
      changeDict[kEmployeeAssignmentStatusID] = AssignmentStatus.Assigned;
    }

    const overrides: QObjDBObject[] = [];
    if (ctx.isPlan) {
      const holderOverride = this.createOverrideWithChange(
        ctx,
        holderObj,
        enumOverrideType.move,
        changeDict
      );
      overrides.push(holderOverride);
    }

    const success = ctx.isPlan
      ? await this.sendBatchUpdate(ctx, overrides)
      : (ctx.overrideClass.SetBoxOverride(holderObj, enumOverrideType.move, { data: changeDict }) ??
        true);
    if (success) {
      const employeeName = sanitizeBlockedValue(holderObj.SmartGetFieldValue("Name"));
      const positionId = positionObj.SmartGetFieldValue("PositionID");

      ctx.wsMgr.BroadcastTopic("add-toast", {
        content: createEmployeePositionClickableToast(
          "Employee {employee} assigned to {position}",
          employeeName as any,
          FormatPositionLabel(positionObj),
          positionId as any
        ),
        status: "success",
        timeoutMS: 10000
      });
    } else {
      ctx.wsMgr.BroadcastTopic("add-toast", {
        message: appstr("Unable to Save Changes"),
        status: "error"
      });
    }

    return success;
  }

  static async ReleaseHolders(dataObjs: QObjDBObject[], partialUnassign?: boolean) {
    const ctx = this.getContext();
    if (!ctx.isValid || !ctx.planMgr) {
      return false;
    }

    const empPosFieldName = this.findFieldNameById(ctx, enumObjClassNames.box, kEmployeePositionID);
    if (!empPosFieldName) {
      return false;
    }

    if (dataObjs.length === 0) {
      return false;
    }

    let messagingMode: "all" | "single" | "mixed" | null = null;
    if (dataObjs.length === 1) {
      messagingMode = "single";
    } else {
      const firstPositionID = dataObjs[0].SmartGetFieldValue(kEmployeePositionID);
      const areAllSamePosition = dataObjs.every(dataObj => {
        return dataObj.SmartGetFieldValue(kEmployeePositionID) === firstPositionID;
      });

      if (areAllSamePosition) {
        messagingMode = "all";
      } else {
        messagingMode = "mixed";
      }
    }

    const overrides: QObjDBObject[] = [];
    const actionID = crypto.randomUUID();

    for (const dataObj of dataObjs) {
      const overrideObj = this.createOverrideWithChange(
        ctx,
        dataObj,
        enumOverrideType.delete,
        {
          [empPosFieldName]: "",
          [kEmployeeAssignmentStatusID]: AssignmentStatus.Released,
          Orphan: 1
        },
        actionID
      );

      overrides.push(overrideObj);
    }

    const success = await this.sendBatchUpdate(ctx, overrides);

    let toastTemplateKey = "";
    let toastReplaceDict = {};

    if (messagingMode === "single") {
      const [holderObj] = dataObjs;
      const positionObj = holderObj.GetParentPosition();
      const employeeName = sanitizeBlockedValue(holderObj.SmartGetFieldValue("Name"));

      if (success) {
        // Temporarily make employee not clickable from toast till Employee list is not added into Tabular view.
        // TODO: After Employee list is added, remove below block.
        if (isInTabularView()) {
          if (!positionObj) {
            // positionObj unavailable in tabular view; suppress clickable toast
          } else {
            ctx.wsMgr.BroadcastTopic("add-toast", {
              content: createEntityUpdatedClickableToast(positionObj, {
                template: "Employee is released from entity",
                value: { employee: employeeName as any }
              }),
              status: "success",
              timeoutMS: 10000
            });
          }
        } else {
          const positionId = positionObj?.SmartGetFieldValue("PositionID");
          ctx.wsMgr.BroadcastTopic("add-toast", {
            content: createEmployeePositionClickableToast(
              "{employee} is released from {position}",
              employeeName as any,
              positionObj ? FormatPositionLabel(positionObj) : "",
              positionId as any
            ),
            status: "success",
            timeoutMS: 10000
          });
        }
      }
    } else if (messagingMode === "all") {
      const [holderObj] = dataObjs;
      const positionObj = holderObj.GetParentPosition();

      const totalHoldersCount = partialUnassign ? dataObjs.length : appstr("All");
      toastTemplateKey = "totalHoldersCount employees are released from entity";
      toastReplaceDict = {
        totalHoldersCountEmployees: `${totalHoldersCount} ${appstr(totalHoldersCount === 1 ? "Employee" : "Employees").toLocaleLowerCase()}`
      };

      if (success) {
        ctx.wsMgr.BroadcastTopic("add-toast", {
          content: createEntityUpdatedClickableToast(positionObj, {
            template: toastTemplateKey,
            value: toastReplaceDict
          }),
          status: "success",
          timeoutMS: 10000
        });
      }
    } else if (messagingMode === "mixed") {
      const totalHoldersCount = partialUnassign ? dataObjs.length : appstr("All");
      toastTemplateKey = "totalHoldersCount employees are released from their positions";
      toastReplaceDict = {
        totalHoldersCountEmployees: `${totalHoldersCount} ${appstr(totalHoldersCount === 1 ? "Employee" : "Employees").toLocaleLowerCase()}`
      };

      if (success) {
        ctx.wsMgr.BroadcastTopic("add-toast", {
          ...createBoldToastMessage(toastTemplateKey, toastReplaceDict),
          status: "success"
        });
      }
    }

    if (!success) {
      ctx.wsMgr.BroadcastTopic("add-toast", {
        message: appstr("Unable to Save Changes"),
        status: "error"
      });
    }

    return success;
  }

  static async MoveBranch(sourceObj: QObjDBObject | null, targetObj: QObjDBObject | null) {
    if (!sourceObj || !targetObj) {
      return false;
    }

    const ctx = this.getContext();
    if (!ctx.isValid) {
      return false;
    }

    if (IsPositionClosed(targetObj)) {
      const alert = new QAlert();
      alert.InitAlert(
        appstr("Cannot move record"),
        appstr("You cannot move this record under a closed position."),
        null,
        ctx.wsMgr
      );
      return false;
    }

    const parentClass = sourceObj.GetParentClass();
    const parentFieldDef = parentClass?.GetSupervisorIDFieldDef();
    const parentFieldName = parentFieldDef?.GetFieldName();
    if (!parentFieldDef) {
      return false;
    }

    const targetParentId = targetObj.GetObjectRecordID();
    const currentParentId = sourceObj.GetFieldValue(parentFieldDef);
    if (currentParentId === targetParentId) {
      return false;
    }

    const changesDict = {
      [parentFieldName]: targetParentId
    };

    let success = false;
    if (ctx.isPlan) {
      const overrideObj = this.createOverrideWithChange(
        ctx,
        sourceObj,
        enumOverrideType.move,
        changesDict
      );
      success = await this.sendBatchUpdate(ctx, [overrideObj]);
    } else {
      success =
        ctx.overrideClass.SetBoxOverride(sourceObj, enumOverrideType.move, { data: changesDict }) ??
        true;
    }

    if (success) {
      ctx.wsMgr.BroadcastTopic("add-toast", {
        content: createMovedFromToPositionClickableToast(
          "Subordinate moved from position to position",
          sourceObj,
          targetObj
        ),
        status: "success",
        timeoutMS: 10000
      });
    } else {
      ctx.wsMgr.BroadcastTopic("add-toast", {
        message: appstr("Unable to Save Changes"),
        status: "error"
      });
    }

    return success;
  }

  static async ClosePosition(dataObjs: QObjDBObject[], shouldRelease: boolean) {
    const ctx = this.getContext();
    if (!ctx.isValid || !ctx.isPlan) {
      return false;
    }

    const empPosFieldName = this.findFieldNameById(ctx, enumObjClassNames.box, kEmployeePositionID);

    if (!empPosFieldName) {
      return false;
    }

    if (dataObjs.length === 0) {
      return false;
    }

    const messagingMode: "single" | "mixed" = dataObjs.length === 1 ? "single" : "mixed";

    const overrides: QObjDBObject[] = [];
    const actionID = crypto.randomUUID();

    for (const dataObj of dataObjs) {
      const holders = dataObj.GetHolderList() ?? [];
      const holderChangeType =
        !shouldRelease || holders.length === 0 ? enumOverrideType.move : enumOverrideType.delete;

      for (const holder of holders) {
        const changeDict: Record<string, unknown> = {
          [empPosFieldName]: "",
          [kEmployeeAssignmentStatusID]: AssignmentStatus.Unassigned,
          Orphan: 1
        };

        if (shouldRelease) {
          changeDict[kEmployeeAssignmentStatusID] = AssignmentStatus.Released;
        }

        const holderOverride = this.createOverrideWithChange(
          ctx,
          holder,
          holderChangeType,
          changeDict,
          actionID
        );

        overrides.push(holderOverride);
      }

      const positionChangeDict: Record<string, unknown> = {
        [kPositionStatusID]: PositionStatus.Closed
      };

      const objDb = ctx.objDb;
      const positionClass = objDb?.GetClassByName(enumObjClassNames.position);
      const staffTargetFieldDef = positionClass?.GetFieldDefByID("Desired Holders");
      const staffTargetFieldName = staffTargetFieldDef?.GetFieldName();
      if (staffTargetFieldName) {
        positionChangeDict[staffTargetFieldName] = 0;
      }

      const positionOverride = this.createOverrideWithChange(
        ctx,
        dataObj,
        enumOverrideType.delete,
        positionChangeDict,
        actionID
      );
      overrides.push(positionOverride);
    }

    const success = await this.sendBatchUpdate(ctx, overrides);

    if (success) {
      let toastTemplateKey = "Entities are closed";
      let toastReplaceKeyOrDict = {};
      let posObj: QObjDBObject | QObjDBObject[] = dataObjs;
      let topicState: any = {
        status: "success"
      };
      if (messagingMode === "single") {
        toastTemplateKey = "The entity is closed";
        toastReplaceKeyOrDict = "entity";
        posObj = dataObjs[0];
        topicState = {
          ...topicState,
          content: createPositionClickableToast(toastTemplateKey, posObj, toastReplaceKeyOrDict),
          timeoutMS: 10000
        };
      } else {
        toastReplaceKeyOrDict = {
          __entities__: `${posObj.length} ${appstr("Positions").toLocaleLowerCase()}`
        };
        topicState = {
          ...topicState,
          ...createBoldToastMessage(toastTemplateKey, toastReplaceKeyOrDict)
        };
      }

      ctx.wsMgr.BroadcastTopic("add-toast", topicState);
    } else {
      ctx.wsMgr.BroadcastTopic("add-toast", {
        message: appstr("Unable to Save Changes"),
        status: "error"
      });
    }

    return success;
  }

  static async OpenPosition(dataObj: QObjDBObject) {
    const ctx = this.getContext();
    if (!ctx.isValid || !ctx.planMgr) {
      return false;
    }

    if (!dataObj.IsPositionObj()) {
      return false;
    }

    const activeOverride = ctx.planMgr.GetActiveOverrideObj(dataObj);
    if (!activeOverride) {
      // Bad state - in order to reopen a position, it must've been closed
      return false;
    }

    const workingOverride = activeOverride.Clone();
    const changeList = workingOverride.fChanges as QChange[];
    for (var i = changeList.length; i > 0; i--) {
      const change = changeList[i - 1];
      const changeType = change.GetChangeType();

      // Changes that are not deletions are ignored
      if (changeType === enumOverrideType.delete && change.IsReverted() !== true) {
        // Re-opening a position should NOT revert the close, rather it is an additive change
        change.fChangeType = enumOverrideType.change;
      }
    }

    const positionChangeDict: Record<string, unknown> = this.createChangeDict(ctx, {
      [kPositionStatusID]: PositionStatus.Active
    });

    const objDb = ctx.objDb;
    const positionClass = objDb?.GetClassByName(enumObjClassNames.position);
    const staffTargetFieldDef = positionClass?.GetFieldDefByID("Desired Holders");
    const staffTargetFieldName = staffTargetFieldDef?.GetFieldName();
    if (staffTargetFieldName) {
      positionChangeDict[staffTargetFieldName] = 1;
    }

    const change = new QChange();
    change.InitChange(ctx.wsMgr, positionChangeDict, workingOverride, dataObj, ctx.overrideClass);
    change.UpdateChange(positionChangeDict, enumOverrideType.change);
    workingOverride.fChanges?.push(change);

    const success = await this.sendBatchUpdate(ctx, [workingOverride]);
    if (success) {
      ctx.wsMgr.BroadcastTopic("add-toast", {
        content: createPositionClickableToast("Entity has been reopened", dataObj, "entity"),
        status: "success",
        timeoutMS: 10000
      });
    } else {
      ctx.wsMgr.BroadcastTopic("add-toast", {
        message: appstr("Unable to Save Changes"),
        status: "error"
      });
    }

    return success;
  }
}
